/**
 * Field Lube Report (reference/Lube_sheet.pdf). Items and lubricants come from the client profile.
 * Each item is Check / Change / N/A; lubricants in lube.supplyRequired need Bulk or Buckets.
 */
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { OTHER_LUBRICANT } from '@wo/shared';
import { api, errorText } from '../../lib/api';
import { dateTime } from '../../lib/format';
import type { LubeView } from '../../lib/form-types';
import { Button, Card, ErrorText, Field, Input, Loading, Select, Textarea } from '../../components/ui';
import { SigImage } from './SignaturesTab';

export function LubeTab({ woId, readOnly, onChange }: { woId: number; readOnly: boolean; onChange: () => void }) {
  const q = useQuery({ queryKey: ['wo-lube', woId], queryFn: () => api.get<LubeView>(`/api/work-orders/${woId}/lube`) });
  if (q.isLoading || !q.data) return <Loading />;
  return <LubeForm key={q.dataUpdatedAt} woId={woId} v={q.data} readOnly={readOnly} onChange={onChange} />;
}

function LubeForm({ woId, v, readOnly, onChange }: { woId: number; v: LubeView; readOnly: boolean; onChange: () => void }) {
  const qc = useQueryClient();
  const [h, setH] = useState({ customerEquipmentNo: v.header.customerEquipmentNo ?? '', equipHours: v.header.hours ?? '', typeOfService: v.header.typeOfService ?? '', nextService: v.header.nextService ?? '', remarks: v.remarks ?? '' });
  const [items, setItems] = useState(v.items.map((i) => ({ ...i, na: i.na ?? false })));
  const [lubes, setLubes] = useState(v.lubricants.map((l) => ({ ...l, gallons: l.gallons ?? '', supply: l.supply ?? '' })));
  const options = v.supplyOptions ?? [];
  const required = v.supplyRequired ?? [];
  const needsSupply = lubes.filter((l) => required.includes(l.lubricant) && l.gallons !== '' && Number(l.gallons) > 0 && !l.supply);
  const [error, setError] = useState<string | null>(null);
  const set = (k: keyof typeof h) => (e: { target: { value: string } }) => setH({ ...h, [k]: e.target.value });

  const save = async (submit = false) => {
    setError(null);
    try {
      let next = await api.put<LubeView>(`/api/work-orders/${woId}/lube`, {
        ...h,
        items,
        lubricants: lubes.filter((l) => l.gallons !== '' || v.lubricants.some((o) => o.lubricant === l.lubricant && o.otherName === l.otherName && o.gallons)).map((l) => ({ lubricant: l.lubricant, otherName: l.otherName, gallons: l.gallons, supply: l.supply })),
      });
      if (submit) next = await api.post<LubeView>(`/api/work-orders/${woId}/lube/submit`);
      qc.setQueryData(['wo-lube', woId], next);
      onChange();
    } catch (e) {
      setError(errorText(e));
    }
  };

  return (
    <div className="space-y-4">
      <ErrorText error={error} />
      <Card title={<span>FIELD LUBE REPORT {v.brandName && <span className="text-slate-500">({v.brandName})</span>} <span className="ml-3 text-slate-500">S/O {v.so}</span></span>}
        actions={!readOnly && <><Button variant="secondary" disabled={needsSupply.length > 0} onClick={() => save()}>Save</Button>{!v.submittedAt && <Button disabled={needsSupply.length > 0} onClick={() => save(true)}>Save and mark done</Button>}</>}>
        {needsSupply.length > 0 && <p className="mb-2 rounded bg-amber-50 p-2 text-sm text-amber-900">Choose {options.join(' or ')} for: {needsSupply.map((l) => l.lubricant).join(', ')}</p>}
        {v.submittedAt ? <p className="mb-2 text-sm text-green-800">Marked done {dateTime(v.submittedAt)}.</p> : null}
        {v.missing.length > 0 && <p className="mb-2 rounded bg-amber-50 p-2 text-sm text-amber-900">Not complete: {v.missing.join('; ')}</p>}
        <div className="grid gap-3 text-sm md:grid-cols-3">
          <div><span className="text-slate-500">Customer name</span><div>{v.header.customerName}</div></div>
          <Field label="Customer equipment number"><Input value={h.customerEquipmentNo} onChange={set('customerEquipmentNo')} disabled={readOnly} /></Field>
          <div><span className="text-slate-500">Equip. make / model / S/N</span><div>{v.header.make} {v.header.model} · {v.header.serialNumber}</div></div>
          <Field label="Hours"><Input value={h.equipHours} onChange={set('equipHours')} inputMode="decimal" disabled={readOnly} /></Field>
          <div><span className="text-slate-500">Machine location</span><div>{v.header.machineLocation ?? '—'}</div></div>
          <Field label="Type of service"><Input value={h.typeOfService} onChange={set('typeOfService')} disabled={readOnly} /></Field>
          <Field label="Next service"><Input value={h.nextService} onChange={set('nextService')} disabled={readOnly} /></Field>
        </div>
      </Card>
      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="Check / Change">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase text-slate-500"><tr><th>Item</th><th className="text-center">Check</th><th className="text-center">Change</th><th className="text-center">N/A</th></tr></thead>
            <tbody className="divide-y">
              {items.map((it, i) => (
                <tr key={it.item}>
                  <td className="py-1">{it.item}</td>
                  {(['checked', 'changed', 'na'] as const).map((k) => (
                    <td key={k} className="text-center">
                      <input
                        type="checkbox"
                        aria-label={`${it.item} ${k === 'na' ? 'N/A' : k === 'checked' ? 'Check' : 'Change'}`}
                        checked={it[k]}
                        disabled={readOnly}
                        // N/A is neither Check nor Change: ticking one clears the other.
                        onChange={(e) => setItems(items.map((x, j) => (j !== i ? x : k === 'na' ? { ...x, na: e.target.checked, ...(e.target.checked ? { checked: false, changed: false } : {}) } : { ...x, [k]: e.target.checked, ...(e.target.checked ? { na: false } : {}) })))}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
        <Card title="Lubricants (gallons)">
          <table className="w-full text-sm">
            <tbody className="divide-y">
              {lubes.map((l, i) => (
                <tr key={`${l.lubricant}:${l.otherName}:${i}`}>
                  <td className="py-1">
                    {l.lubricant === OTHER_LUBRICANT ? (
                      <Input value={l.otherName} placeholder="Other (name)" disabled={readOnly || v.lubricants.some((o) => o.lubricant === OTHER_LUBRICANT && o.otherName === l.otherName && l.otherName !== '')} onChange={(e) => setLubes(lubes.map((x, j) => (j === i ? { ...x, otherName: e.target.value } : x)))} />
                    ) : (
                      l.lubricant
                    )}
                  </td>
                  <td className="w-28"><Input value={l.gallons} inputMode="decimal" disabled={readOnly} onChange={(e) => setLubes(lubes.map((x, j) => (j === i ? { ...x, gallons: e.target.value } : x)))} /></td>
                  <td className="w-32 pl-1">
                    {(required.includes(l.lubricant) || l.supply) && options.length > 0 && (
                      <Select value={l.supply} disabled={readOnly} aria-label={`${l.lubricant} supply`} onChange={(e) => setLubes(lubes.map((x, j) => (j === i ? { ...x, supply: e.target.value } : x)))}>
                        <option value="">{required.includes(l.lubricant) ? 'Bulk or Buckets?' : '—'}</option>
                        {options.map((o) => <option key={o}>{o}</option>)}
                      </Select>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!readOnly && <Button variant="ghost" className="mt-2" onClick={() => setLubes([...lubes, { lubricant: OTHER_LUBRICANT, otherName: '', gallons: '', supply: '' }])}>+ Other lubricant</Button>}
        </Card>
      </div>
      <Card title="Remarks">
        <Textarea rows={4} value={h.remarks} onChange={set('remarks')} disabled={readOnly} autoCapitalize="sentences" spellCheck />
        <p className="mt-1 text-xs text-slate-500">You can dictate here with the computer's dictation, same as the note boxes.</p>
      </Card>
      {v.signatures && (
        <Card title="Signatures">
          <div className="grid gap-4 md:grid-cols-2">
            <SigImage label="Customer signature" sig={v.signatures.customer} />
            <SigImage label="Technician signature" sig={v.signatures.technician} />
          </div>
        </Card>
      )}
    </div>
  );
}
